import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import type { FeedSlice } from "../types"

interface FeedStoreState {
  feeds: Record<string, FeedSlice>
}

interface FeedStoreActions {
  setFeed: (key: string, slice: FeedSlice) => void
  appendFeed: (key: string, postIds: string[], page: number, hasMore: boolean) => void
  invalidate: (key: string) => void
  invalidateMatching: (substring: string) => void
  clearFeeds: () => void
}

/** 外部注入的 dedup 缓存清除回调（由 request-dedup.ts 注册） */
let onInvalidate: ((cacheKeyPrefix: string) => void) | undefined

export function registerInvalidateCallback(cb: (cacheKeyPrefix: string) => void): void {
  onInvalidate = cb
}

function uniqueIds(ids: string[]): string[] {
  return Array.from(new Set(ids))
}

export const useFeedStore = create<FeedStoreState & FeedStoreActions>()(
  immer((set, get) => ({
    feeds: {},

    setFeed: (key, slice) => set((state) => {
      state.feeds[key] = { ...slice, postIds: uniqueIds(slice.postIds) }
    }),

    appendFeed: (key, postIds, page, hasMore) =>
      set((state) => {
        const existing = state.feeds[key]
        if (existing) {
          existing.postIds = uniqueIds([...existing.postIds, ...postIds])
          existing.page = page
          existing.hasMore = hasMore
        }
      }),

    invalidate: (key) => {
      set((state) => {
        const feed = state.feeds[key]
        if (feed) { feed.staleAt = 0 }
      })
      onInvalidate?.(`feed:${key}`)
    },

    invalidateMatching: (substring) => {
      const keys = Object.keys(get().feeds).filter((k) => k.includes(substring))
      set((state) => {
        for (const key of keys) {
          const feed = state.feeds[key]
          if (feed) feed.staleAt = 0
        }
      })
      for (const key of keys) {
        onInvalidate?.(`feed:${key}`)
      }
    },

    clearFeeds: () => {
      set((state) => { state.feeds = {} })
      onInvalidate?.("feed:")
    },
  }))
)
